import React, { useState, useEffect } from 'react';
import { AppBar, Toolbar, Button, Box, Typography, Divider } from '@mui/material';
import { Link, useLocation } from 'react-router-dom';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { jwtDecode } from 'jwt-decode';
import NotificationBell from '../components/NotificationBell';

const ALL_MENU_ITEMS = [
  { label: 'Sample Registration', path: '/sample-registration-list', module: 'sample_registration' },
  { label: 'Sample Preparation', path: '/sample-preparation-view', module: 'sample_preparation' },
  { label: 'Library Preparation', path: '/Library-Preparation-view', module: 'library_preparation' },
  { label: 'Sequencing', path: '/Sequencing-view', module: 'sequencing' },
  { label: 'BioInformatics Data Analysis', path: '/Bioinformatics-view', module: 'bioinformatics' },
];

// Roles that see all menu items — no filtering needed
const FULL_ACCESS_ROLES = ['Super admin', 'Admin', 'sales', 'lab', 'bio info', 'bio IT', 'finance'];

const Navbar = () => {
  const location = useLocation();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [menuItems, setMenuItems] = useState(ALL_MENU_ITEMS);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const decoded = jwtDecode(token);

      // Full access roles → show all items (existing behaviour unchanged)
      if (FULL_ACCESS_ROLES.includes(decoded.role)) {
        setMenuItems(ALL_MENU_ITEMS);
        return;
      }

      // Custom role → filter by permissions
      if (decoded.role === 'Custom') {
        const permissions = JSON.parse(localStorage.getItem('permissions') || '{}');
        const allowed = ALL_MENU_ITEMS.filter(item => {
          const perms = permissions[item.module];
          return perms && perms.view === true;
        });
        setMenuItems(allowed);
        return;
      }

      setMenuItems([]);
    } catch {
      setMenuItems(ALL_MENU_ITEMS);
    }
  }, [location.pathname]);

  const formattedTime = currentTime.toLocaleTimeString('en-IN', {
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    hour12: false, timeZone: 'Asia/Kolkata',
  });
  const formattedDate = currentTime.toLocaleDateString('en-IN', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    timeZone: 'Asia/Kolkata',
  });

  return (
    <AppBar
      position="static"
      elevation={0}
      sx={{
        background: 'linear-gradient(90deg, #3d5191 0%, #45599d 100%)',
        color: '#fff',
        borderBottom: '1px solid rgba(255,255,255,0.08)',
        boxShadow: '0 4px 18px rgba(30,45,90,0.18)',
        mb: 2,
      }}
    >
      <Toolbar sx={{ display: 'flex', justifyContent: 'space-between', minHeight: 56, px: { xs: 1.5, md: 2.5 }, gap: 1 }}>
        {/* ── Navigation links ── */}
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
          {menuItems.map(({ label, path }) => {
            const isActive = location.pathname.toLowerCase().startsWith(path.toLowerCase());
            return (
              <Button
                key={label}
                component={Link}
                to={path}
                disableRipple
                sx={{
                  position: 'relative',
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  letterSpacing: 0.2,
                  px: 1.75,
                  py: 0.75,
                  borderRadius: 2,
                  color: isActive ? '#fff' : 'rgba(255,255,255,0.78)',
                  fontWeight: isActive ? 600 : 500,
                  backgroundColor: isActive ? 'rgba(255,255,255,0.16)' : 'transparent',
                  transition: 'background-color 0.2s ease, color 0.2s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.12)',
                    color: '#fff',
                  },
                  // slim accent under the active item
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: 3,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: isActive ? '55%' : '0%',
                    height: '3px',
                    borderRadius: 3,
                    backgroundColor: '#ffffff',
                    transition: 'width 0.25s ease',
                  },
                  '&:hover::after': { width: '55%' },
                }}
              >
                {label}
              </Button>
            );
          })}
        </Box>

        {/* ── Right cluster: bell + date/time ── */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 38,
              height: 38,
              borderRadius: 2,
              backgroundColor: 'rgba(255,255,255,0.12)',
              transition: 'background-color 0.2s ease',
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' },
            }}
          >
            <NotificationBell />
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              backgroundColor: 'rgba(255,255,255,0.12)',
              border: '1px solid rgba(255,255,255,0.14)',
              borderRadius: 2,
              px: 1.5,
              py: 0.75,
            }}
          >
            <CalendarTodayIcon sx={{ fontSize: '0.95rem', color: 'rgba(255,255,255,0.75)' }} />
            <Typography variant="body2" sx={{ color: '#fff', fontWeight: 500, letterSpacing: 0.3 }}>
              {formattedDate}
            </Typography>
            <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.22)', my: 0.25 }} />
            <AccessTimeIcon sx={{ fontSize: '0.95rem', color: 'rgba(255,255,255,0.75)' }} />
            <Typography
              variant="body2"
              sx={{ color: '#fff', fontWeight: 600, letterSpacing: 0.6, fontVariantNumeric: 'tabular-nums', minWidth: 68 }}
            >
              {formattedTime}
            </Typography>
          </Box>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Navbar;